/**
 * PosteingangBatchDialoge – Enhanced Batch-Analyse-Ergebnisse mit Claude
 * Zeigt: Absender, Betreuter, Handlungsauftrag, Frist, Dringlichkeit + Aufgabe-anlegen Button
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle
} from '@/components/ui/card';
import {
  Sparkles, Loader2, Check, ChevronRight, AlertCircle,
  User, Building2, X, Plus, CalendarClock, Scale, ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function PosteingangBatchDialoge({
  showBatchDialog,
  setShowBatchDialog,
  batchResults,
  autoZuordnungRunning,
  autoZuordnungResult,
  setAutoZuordnungResult,
  zuordenbarCount,
  selectFromBatch,
  setShowAutoZuordnungConfirm,
  showAutoZuordnungConfirm,
  autoZuordnungOptionen,
  setAutoZuordnungOptionen,
  handleAutoZuordnung
}) {
  const [creatingTask, setCreatingTask] = useState(null);
  const [createdTasks, setCreatedTasks] = useState(new Set());

  const handleCreateTask = async (result) => {
    if (!result.gefundener_klient?.id) {
      toast.error('Kein Betreuter zugeordnet – Aufgabe kann nicht erstellt werden');
      return;
    }
    const aufgabe = result.aufgabe_vorschlag || {};
    setCreatingTask(result.dokument_id);

    try {
      const res = await fetch(`${BACKEND_URL}/api/wiki/batch-aufgabe-erstellen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          klient_id: result.gefundener_klient.id,
          titel: aufgabe.titel || result.handlungsauftrag || result.vorgeschlagener_titel || 'Neue Aufgabe',
          prioritaet: aufgabe.prioritaet || result.prioritaet || 'mittel',
          beschreibung: result.handlungsauftrag || aufgabe.beschreibung || '',
          frist: result.frist || null,
          dokument_id: result.dokument_id,
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Aufgabe "${data.titel}" erstellt`);
        setCreatedTasks(prev => new Set([...prev, result.dokument_id]));
      } else {
        toast.error(data.error || 'Fehler beim Erstellen');
      }
    } catch {
      toast.error('Fehler');
    } finally {
      setCreatingTask(null);
    }
  };

  if (!showBatchDialog) return null;

  return (
    <div className="space-y-4" data-testid="batch-dialoge-container">
      <Card className="border-violet-200 shadow-lg" data-testid="batch-results-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-500" />
              Claude Analyse – Posteingang
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => {
              setShowBatchDialog(false);
              setAutoZuordnungResult(null);
            }}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          {batchResults && (
            <div className="flex items-center gap-4 mt-2">
              <Badge className="bg-red-100 text-red-700">
                {batchResults.zusammenfassung?.hoch || 0} dringend
              </Badge>
              <Badge className="bg-amber-100 text-amber-700">
                {batchResults.zusammenfassung?.mittel || 0} mittel
              </Badge>
              <Badge className="bg-green-100 text-green-700">
                {batchResults.zusammenfassung?.niedrig || 0} niedrig
              </Badge>
              <Badge variant="outline" className="text-xs">
                Claude Sonnet 4.6
              </Badge>
            </div>
          )}
        </CardHeader>

        <CardContent>
          <div className="max-h-[50vh] overflow-y-auto space-y-3 pr-2">
            {batchResults?.ergebnisse?.map((result, idx) => (
              <div
                key={result.dokument_id}
                className={`p-3 rounded-lg border transition-shadow ${
                  result.prioritaet === 'hoch'
                    ? 'border-red-200 bg-red-50'
                    : result.prioritaet === 'mittel'
                    ? 'border-amber-200 bg-amber-50'
                    : 'border-slate-200 bg-slate-50'
                }`}
                data-testid={`batch-result-${idx}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-sm font-bold ${
                      result.prioritaet === 'hoch'
                        ? 'bg-red-200 text-red-700'
                        : result.prioritaet === 'mittel'
                        ? 'bg-amber-200 text-amber-700'
                        : 'bg-slate-200 text-slate-700'
                    }`}>
                      {result.position}
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Titel */}
                      <p className="font-medium text-slate-800 truncate cursor-pointer hover:text-violet-700"
                        onClick={() => selectFromBatch(result)}>
                        {result.vorgeschlagener_titel || result.dateiname}
                      </p>

                      {/* Absender + Betreuter */}
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        {result.absender && (
                          <Badge variant="outline" className="text-[11px] py-0">
                            <Building2 className="w-3 h-3 mr-1" />
                            {result.absender}
                          </Badge>
                        )}
                        {result.gefundener_klient ? (
                          <Badge className="bg-violet-100 text-violet-700 text-[11px] py-0">
                            <User className="w-3 h-3 mr-1" />
                            {result.gefundener_klient.vorname} {result.gefundener_klient.nachname}
                          </Badge>
                        ) : result.betrifft_person ? (
                          <Badge variant="outline" className="text-[11px] py-0 border-orange-300 text-orange-700">
                            <User className="w-3 h-3 mr-1" />
                            {result.betrifft_person} (unbekannt)
                          </Badge>
                        ) : null}
                        {result.aufgabenkreis && (
                          <Badge variant="outline" className="text-[11px] py-0">
                            <Scale className="w-3 h-3 mr-1" />
                            {result.aufgabenkreis}
                          </Badge>
                        )}
                      </div>

                      {/* Handlungsauftrag */}
                      {result.handlungsauftrag && (
                        <div className="mt-2 text-sm text-slate-700 bg-white/60 rounded px-2 py-1.5 border border-slate-200">
                          <span className="font-medium text-violet-700">Handlungsauftrag: </span>
                          {result.handlungsauftrag}
                        </div>
                      )}

                      {/* Frist + Rechtliche Grundlage */}
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {result.frist && (
                          <Badge className="bg-red-100 text-red-700 text-[11px]">
                            <CalendarClock className="w-3 h-3 mr-1" />
                            Frist: {result.frist}
                            {result.frist_begruendung ? ` (${result.frist_begruendung})` : ''}
                          </Badge>
                        )}
                        {result.rechtliche_grundlage && (
                          <Badge variant="outline" className="text-[11px]">
                            <Scale className="w-3 h-3 mr-1" />
                            {result.rechtliche_grundlage}
                          </Badge>
                        )}
                        {result.empfohlene_aktion && (
                          <Badge className={`text-[11px] ${
                            result.empfohlene_aktion === 'aufgabe_anlegen'
                              ? 'bg-blue-100 text-blue-700'
                              : result.empfohlene_aktion === 'antwort_schreiben'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-slate-100 text-slate-700'
                          }`}>
                            {result.empfohlene_aktion === 'aufgabe_anlegen' ? 'Aufgabe anlegen' :
                             result.empfohlene_aktion === 'antwort_schreiben' ? 'Antwort schreiben' :
                             result.empfohlene_aktion === 'weiterleiten' ? 'Weiterleiten' :
                             result.empfohlene_aktion === 'zur_akte' ? 'Ablegen' :
                             result.empfohlene_aktion}
                          </Badge>
                        )}
                      </div>

                      {/* Begründung */}
                      {result.begruendung && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                          {result.begruendung}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Aufgabe anlegen Button */}
                  <div className="flex flex-col gap-1 shrink-0">
                    {result.gefundener_klient?.id && !createdTasks.has(result.dokument_id) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7 px-2 border-violet-300 text-violet-700 hover:bg-violet-50"
                        onClick={(e) => { e.stopPropagation(); handleCreateTask(result); }}
                        disabled={creatingTask === result.dokument_id}
                        data-testid={`batch-create-task-${idx}`}
                      >
                        {creatingTask === result.dokument_id ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <Plus className="w-3 h-3 mr-1" />
                        )}
                        Aufgabe
                      </Button>
                    ) : createdTasks.has(result.dokument_id) ? (
                      <Badge className="bg-green-100 text-green-700 text-[10px]">
                        <Check className="w-3 h-3 mr-0.5" /> Erstellt
                      </Badge>
                    ) : null}
                    <button
                      className="text-slate-400 hover:text-violet-600 p-1"
                      onClick={() => selectFromBatch(result)}
                      title="Details anzeigen"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {batchResults?.fehler?.length > 0 && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-medium text-red-700 mb-2">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  {batchResults.fehler.length} Dokument(e) konnten nicht analysiert werden
                </p>
                {batchResults.fehler.map((f, i) => (
                  <p key={i} className="text-xs text-red-600">{f.dateiname}: {f.fehler}</p>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t mt-4">
            {zuordenbarCount > 0 && !autoZuordnungResult && (
              <Button
                onClick={() => setShowAutoZuordnungConfirm(true)}
                disabled={autoZuordnungRunning}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="auto-zuordnung-btn"
              >
                {autoZuordnungRunning ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                {autoZuordnungRunning ? 'Ordne zu...' : `${zuordenbarCount} automatisch zuordnen`}
              </Button>
            )}
            {autoZuordnungResult && (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <Check className="w-4 h-4" />
                {autoZuordnungResult.anzahl_zugeordnet} zugeordnet
              </div>
            )}
            <Button variant="outline" onClick={() => {
              setShowBatchDialog(false);
              setAutoZuordnungResult(null);
            }}>
              Schließen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Auto-Zuordnung Bestätigung */}
      {showAutoZuordnungConfirm && (
        <Card className="border-violet-200 shadow-lg" data-testid="auto-zuordnung-confirm-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-500" />
                Auto-Zuordnung bestätigen
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowAutoZuordnungConfirm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Möchten Sie {zuordenbarCount} Dokument(e) automatisch den erkannten Klienten zuordnen?
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="text-sm space-y-1 list-disc list-inside text-slate-600">
              <li>Dokumente werden dem erkannten Klienten zugeordnet</li>
              <li>Vorgeschlagener Titel wird übernommen</li>
              <li>Dokumentstatus wird auf "zugeordnet" gesetzt</li>
            </ul>
            <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoZuordnungOptionen.auch_aufgaben_erstellen}
                  onChange={(e) => setAutoZuordnungOptionen(opt => ({
                    ...opt, auch_aufgaben_erstellen: e.target.checked
                  }))}
                  className="mt-0.5 h-4 w-4 rounded border-violet-300 text-violet-600 focus:ring-violet-500"
                  data-testid="aufgaben-erstellen-checkbox"
                />
                <div>
                  <p className="text-sm font-medium text-violet-800">Aufgaben automatisch erstellen</p>
                  <p className="text-xs text-violet-600 mt-0.5">
                    Erstellt vorgeschlagene Aufgaben für dringende Dokumente
                  </p>
                </div>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAutoZuordnungConfirm(false)}>Abbrechen</Button>
              <Button onClick={handleAutoZuordnung} className="bg-violet-600 hover:bg-violet-700" data-testid="confirm-auto-zuordnung-btn">
                <Check className="w-4 h-4 mr-2" /> Ja, zuordnen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
